/**
 * "Set quantity for all lines": one quantity for many lines in one write (ease round,
 * 13 Oct 2026: a mail without quantities cost one dialog per line).
 *
 * The person picks the lines (the lines without a given quantity, that is with none or
 * with the default 1 of 14 Oct 2026, all lines, or one group of the draft), types a
 * whole number and, when wanted, a unit. Enter saves. The write is
 * `writes.setQuantities`: one version step, one history entry, every cell shows the value
 * at once, and the toast carries "Undo" (every line reads as before: what I2Q read, or
 * the quantity a person had set). When the chosen lines hold quantities already, the
 * dialog says how many the save replaces. A price a person set on a line stays. Nothing
 * is sent and nothing reaches F&O.
 *
 * `quotableRows` and `quantityScopes` are pure, so the strip, the checklist and the tests
 * read the same lists.
 */

import { Button, Dialog, Field, Input, Select, Typography } from "@norican/react";
import { useState } from "react";

import { type DraftRow } from "@/features/case-review/draft-line-groups";
import {
  isAddedLine,
  QTY_WHOLE_ERROR,
  wholeQuantity,
} from "@/features/case-review/line-corrections";
import { type CaseWrites } from "@/features/case-review/use-case-writes";
import { logger } from "@/lib/logger";
import { ACTION_LABELS, DRAFT_GROUPS, LINE_STATUS_WORDS, QUANTITY_WORDS } from "@/lib/sales-flow";

/**
 * The rows a quantity can be set on: lines of I2Q with a key that are still to quote. A
 * line a person decided as "No item" and a line sales added (its own dialog) are left out.
 */
export function quotableRows(rows: readonly DraftRow[]): DraftRow[] {
  return rows.filter(
    (row) =>
      Boolean(row.line.lineKey) &&
      row.lineIndex >= 0 &&
      !isAddedLine(row.line) &&
      row.word !== LINE_STATUS_WORDS.noItem,
  );
}

/**
 * True when the row has a quantity that the mail, a document or a person gave. The
 * default 1 that I2Q uses when the mail gave none (14 Oct 2026) is no given quantity.
 */
export function hasGivenQuantity(row: DraftRow): boolean {
  return row.line.qty != null && !row.line.qtyDefault;
}

/** The keys of the rows to quote without a given quantity (none, or the default 1). */
export function missingQuantityKeys(rows: readonly DraftRow[]): string[] {
  return quotableRows(rows)
    .filter((row) => !hasGivenQuantity(row))
    .map((row) => row.line.lineKey ?? "");
}

/**
 * How many of the named lines hold a given quantity already: the dialog says so before a
 * save replaces them (review of the ease round, 13 Oct 2026: "All lines" wrote over the
 * quantities of the mail and over a typed one without a word). The default 1 is not
 * counted: a save is meant to replace it.
 */
export function withQuantityCount(rows: readonly DraftRow[], lineKeys: readonly string[]): number {
  const keys = new Set(lineKeys);
  return quotableRows(rows).filter(
    (row) => hasGivenQuantity(row) && keys.has(row.line.lineKey ?? ""),
  ).length;
}

/** One choice of the "Lines" box: its words and the lines it stands for. */
export interface QuantityScope {
  id: "missing" | "all" | "needs_you" | "matched";
  label: string;
  lineKeys: string[];
}

/**
 * The choices of the "Lines" box, in this order: the lines without a given quantity (with their
 * count, only when there are any), all lines, then each group of the draft that holds
 * some but not all of the lines. The first entry is the one the dialog starts with.
 */
export function quantityScopes(rows: readonly DraftRow[]): QuantityScope[] {
  const quotable = quotableRows(rows);
  const keysOf = (list: readonly DraftRow[]) => list.map((row) => row.line.lineKey ?? "");
  const scopes: QuantityScope[] = [];
  const missing = missingQuantityKeys(rows);
  if (missing.length > 0) {
    scopes.push({
      id: "missing",
      label: `${QUANTITY_WORDS.scopeMissing} (${missing.length})`,
      lineKeys: missing,
    });
  }
  scopes.push({ id: "all", label: QUANTITY_WORDS.scopeAll, lineKeys: keysOf(quotable) });
  for (const [id, group] of [
    ["needs_you", DRAFT_GROUPS.needsYou],
    ["matched", DRAFT_GROUPS.matched],
  ] as const) {
    const members = quotable.filter((row) => row.group === group);
    if (members.length > 0 && members.length < quotable.length) {
      scopes.push({ id, label: `${group} (${members.length})`, lineKeys: keysOf(members) });
    }
  }
  return scopes;
}

export interface BulkQuantityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  /** The rows of the draft, as the table shows them. */
  rows: readonly DraftRow[];
  writes: CaseWrites;
}

export function BulkQuantityDialog({
  open,
  onOpenChange,
  caseId,
  rows,
  writes,
}: BulkQuantityDialogProps) {
  const scopes = quantityScopes(rows);
  const [scopeId, setScopeId] = useState<QuantityScope["id"]>(scopes[0]?.id ?? "all");
  const [qtyText, setQtyText] = useState("");
  const [unit, setUnit] = useState("");
  const scope = scopes.find((entry) => entry.id === scopeId) ?? scopes[0];
  const replaced = scope ? withQuantityCount(rows, scope.lineKeys) : 0;
  const qty = wholeQuantity(qtyText);
  const qtyInvalid = qtyText.trim() !== "" && qty === null;
  const ready = qty !== null && Boolean(scope) && (scope?.lineKeys.length ?? 0) > 0;

  const save = () => {
    if (!ready || qty === null || !scope) {
      return;
    }
    logger.debug("[case] set quantity for many lines", {
      case: caseId,
      scope: scope.id,
      lines: scope.lineKeys.length,
    });
    writes.setQuantities(scope.lineKeys, qty, unit.trim() || undefined, () => onOpenChange(false));
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="sm" data-bulk-quantity-dialog>
          {/* A form, so Enter in a box saves. `contents`: the popup lays out its parts. */}
          <form
            className="contents"
            onSubmit={(event) => {
              event.preventDefault();
              save();
            }}
          >
            <Dialog.Header>
              <Dialog.Title>{QUANTITY_WORDS.dialogTitle}</Dialog.Title>
              <Dialog.CloseButton />
            </Dialog.Header>
            <Dialog.Body className="flex flex-col gap-4">
              <Dialog.Description>
                {/* A span: the description is a paragraph already. */}
                <Typography as="span" variant="body-sm" tone="muted">
                  {QUANTITY_WORDS.dialogNote}
                </Typography>
              </Dialog.Description>
              <Field label="Lines">
                <Select.Root
                  value={scope?.id ?? "all"}
                  onValueChange={(next) => {
                    const found = scopes.find((entry) => entry.id === next);
                    if (found) {
                      setScopeId(found.id);
                    }
                  }}
                >
                  <Select.Trigger aria-label="Lines" className="w-full">
                    <Select.Value>
                      {(value: unknown) =>
                        scopes.find((entry) => entry.id === value)?.label ?? QUANTITY_WORDS.scopeAll
                      }
                    </Select.Value>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Positioner>
                      <Select.Popup>
                        <Select.List>
                          {scopes.map((entry) => (
                            <Select.Item key={entry.id} value={entry.id}>
                              {entry.label}
                            </Select.Item>
                          ))}
                        </Select.List>
                      </Select.Popup>
                    </Select.Positioner>
                  </Select.Portal>
                </Select.Root>
              </Field>
              {replaced > 0 && (
                // The chosen lines hold quantities: the person reads it before the save.
                <Typography variant="body-sm" className="text-warning-fg" data-quantity-replaces>
                  {QUANTITY_WORDS.replaces(replaced)}
                </Typography>
              )}
              <Field
                label="Quantity"
                hint="A whole number."
                error={qtyInvalid ? QTY_WHOLE_ERROR : undefined}
              >
                <Input
                  value={qtyText}
                  inputMode="numeric"
                  onChange={(event) => setQtyText(event.target.value)}
                  onFocus={(event) => event.target.select()}
                  aria-label="Quantity"
                  autoFocus
                />
              </Field>
              <Field
                label="Unit (optional)"
                hint="As the customer wrote it, for example pcs, box or set."
              >
                <Input
                  value={unit}
                  maxLength={16}
                  onChange={(event) => setUnit(event.target.value)}
                  aria-label="Unit"
                />
              </Field>
              {writes.error && (
                <Typography variant="body-sm" className="text-danger-fg" role="alert">
                  {writes.error}
                </Typography>
              )}
            </Dialog.Body>
            <Dialog.Footer className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" tone="neutral" onClick={() => onOpenChange(false)}>
                {ACTION_LABELS.cancel}
              </Button>
              <Button
                type="submit"
                tone="accent"
                variant="solid"
                loading={writes.busy}
                disabled={!ready}
              >
                {ACTION_LABELS.saveQuantity}
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
